/**
 * ComparisonPage — fully crawlable, SEO/GEO/AEO-optimised comparison page.
 *
 * Route: /compare/:slug  (e.g. /compare/crown-and-rose-vs-scrivener)
 *
 * Bot / crawler strategy
 * ──────────────────────
 * 1. Comprehensive JSON-LD @graph — parsed by ALL bots before JS executes:
 *    WebPage · Article · BreadcrumbList · two SoftwareApplication entities ·
 *    ItemList (feature comparison rows) · ItemList (CR exclusives) · FAQPage
 * 2. OG + Twitter meta for social previews and AI-engine rich-cards.
 * 3. robots max-snippet:-1 — lets Google display full text snippets.
 * 4. <link rel="alternate" type="application/json"> — signals the machine-readable
 *    API endpoint to direct JSON scrapers.
 * 5. sr-only semantic HTML table — readable by curl scrapers, screen readers
 *    and any bot that does not execute JS.
 * 6. PublicNavBar — crawlable site-wide navigation so bots discover all pages.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { comparisonApi } from '@/lib/api';
import CompetitorMatrix from '@/components/comparison/CompetitorMatrix';
import PublicNavBar from '@/components/landing/PublicNavBar';

const SITE_URL = (process.env.REACT_APP_BACKEND_URL || 'https://crownnrose.com').replace('/api', '');
const API_ROOT = process.env.REACT_APP_BACKEND_URL || `${SITE_URL}/api`;

// Official domains for each competitor — used as sameAs entity links in JSON-LD
// so that AI citation engines can resolve the entity to the canonical web presence.
const COMPETITOR_SAME_AS = {
  scrivener:     'https://www.literatureandlatte.com/scrivener/overview',
  atticus:       'https://www.atticus.io',
  vellum:        'https://www.vellum.pub',
  prowritingaid: 'https://prowritingaid.com',
  jasper:        'https://www.jasper.ai',
  novelcrafter:  'https://novelcrafter.com',
  sudowrite:     'https://www.sudowrite.com',
  novelai:       'https://novelai.net',
  grammarly:     'https://www.grammarly.com',
};

// Safely serialise JSON-LD for inline <script> injection.
// JSON.stringify does not escape </script>, which can break the page or
// cause parser confusion. Replace with Unicode escapes.
function safeJsonLd(obj) {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

// Crown & Rose canonical feature list — used in SoftwareApplication JSON-LD
const CR_FEATURES = [
  'AI manuscript drafting with Claude, GPT-4o, and Gemini in one platform',
  'Bulk novel generation — entire book in one automated job',
  'Edit Walker — dual-pass developmental editing: Opus analysis then prose rewrite',
  'Continuity audit with automatic drift correction and nightly auto-walk scheduling',
  'Arc Visualizer — deterministic per-character arc scoring across all chapters',
  'Voice Coach — series-level prose voice memory for consistent style across books',
  'Plot Readiness Pill — 6-element structural analysis from Exposition to Resolution',
  'Marketing Asset Generator — Amazon blurb, ARC pitch email, tweet thread, social calendar, reviewer CRM',
  'Prompt caching — reduces LLM cost ~37% across Edit Walker and Continuity Review',
  'Mixed-model support — assign different models for drafting vs. in-manuscript chat',
  'Multi-language platform — English, French, German, Spanish, Portuguese, Chinese',
];

/**
 * Build a fully populated JSON-LD @graph for a comparison page.
 * Includes WebPage, Article, BreadcrumbList, two SoftwareApplication nodes,
 * two ItemList nodes (full feature table + CR exclusives), and FAQPage.
 */
function buildGraph(page, slug) {
  const url   = `${SITE_URL}/compare/${slug}`;
  const pubAt = page.generated_at || new Date().toISOString();
  const competitorKey = slug.replace('crown-and-rose-vs-', '');
  const competitorUrl = COMPETITOR_SAME_AS[competitorKey];

  return {
    '@context': 'https://schema.org',
    '@graph': [
      // ── WebPage ──────────────────────────────────────────────────────────
      {
        '@type': 'WebPage',
        '@id':   `${url}#webpage`,
        url,
        name:        page.title,
        description: page.meta_description,
        inLanguage:  'en-US',
        datePublished: pubAt,
        dateModified:  pubAt,
        isPartOf: {
          '@type': 'WebSite',
          '@id':   `${SITE_URL}/#website`,
          url:     SITE_URL,
          name:    'Crown & Rose',
        },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: `${SITE_URL}/brand/crownandrose-logo.png`,
        },
        about: [
          { '@id': `${url}#crown-and-rose` },
          { '@id': `${url}#competitor` },
        ],
        potentialAction: {
          '@type': 'ReadAction',
          target: url,
        },
      },

      // ── Article (for AI-engine citation) ─────────────────────────────────
      {
        '@type': 'Article',
        '@id':   `${url}#article`,
        isPartOf:      { '@id': `${url}#webpage` },
        headline:      page.title,
        description:   page.hero_subhead || page.meta_description,
        abstract:      page.summary_verdict,
        inLanguage:    'en-US',
        datePublished: pubAt,
        dateModified:  pubAt,
        author: {
          '@type': 'Organization',
          name: 'Crown & Rose LLC',
          url:  SITE_URL,
        },
        publisher: {
          '@type': 'Organization',
          name: 'Crown & Rose LLC',
          url:  SITE_URL,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/brand/crownandrose-logo.png` },
        },
        keywords:  page.meta_keywords,
        mentions:  [
          { '@id': `${url}#crown-and-rose` },
          { '@id': `${url}#competitor` },
        ],
      },

      // ── BreadcrumbList ────────────────────────────────────────────────────
      {
        '@type': 'BreadcrumbList',
        '@id':   `${url}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Crown & Rose', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Comparisons',  item: `${SITE_URL}/compare` },
          { '@type': 'ListItem', position: 3, name: page.title,     item: url },
        ],
      },

      // ── Crown & Rose SoftwareApplication ─────────────────────────────────
      {
        '@type': 'SoftwareApplication',
        '@id':   `${url}#crown-and-rose`,
        name:    'Crown & Rose',
        sameAs:  [SITE_URL, 'https://crownnrose.com'],
        description: 'Full-novel production & management system for indie authors. Combines mixed-model AI drafting (Claude, GPT, Gemini), systematic developmental editing via Edit Walker, continuity audit with drift correction, arc visualizer with deterministic chapter scoring, voice coach with series-level memory, and a complete marketing asset suite.',
        url:     SITE_URL,
        applicationCategory:    'UtilitiesApplication',
        applicationSubCategory: 'AI Novel Writing Software',
        operatingSystem: 'Web Browser',
        softwareVersion: '2.38',
        featureList: CR_FEATURES,
        offers: {
          '@type': 'AggregateOffer',
          lowPrice:  '29.00',
          highPrice: '399.00',
          priceCurrency: 'USD',
          offerCount: 3,
          offers: [
            { '@type': 'Offer', name: 'Author',    price: '39.00',  priceCurrency: 'USD', description: '$39/month — author-tier AI writing plan, 1.5M tokens' },
            { '@type': 'Offer', name: 'Publisher', price: '119.00', priceCurrency: 'USD', description: '$119/month — publisher-tier with full novel review, 4.5M tokens' },
            { '@type': 'Offer', name: 'Studio',    price: '449.00', priceCurrency: 'USD', description: '$449/month — studio tier, all capabilities unlocked, 20M tokens' },
          ],
        },
        provider: {
          '@type': 'Organization',
          name: 'Crown & Rose LLC',
          url:  SITE_URL,
        },
      },

      // ── Competitor SoftwareApplication ────────────────────────────────────
      {
        '@type': 'SoftwareApplication',
        '@id':   `${url}#competitor`,
        name:    page.competitor_name,
        ...(competitorUrl && { sameAs: competitorUrl }),
        description: (page.competitor_pros || []).join('. '),
        applicationCategory: 'UtilitiesApplication',
        offers: {
          '@type': 'Offer',
          description: `Pricing for ${page.competitor_name}`,
        },
      },

      // ── ItemList — full feature comparison table ──────────────────────────
      {
        '@type': 'ItemList',
        '@id':   `${url}#feature-comparison`,
        name:    `Feature Comparison: Crown & Rose vs ${page.competitor_name}`,
        description: `A detailed feature-by-feature comparison between Crown & Rose and ${page.competitor_name} covering AI drafting, structural tools, editing, continuity, marketing and pricing.`,
        numberOfItems: (page.comparison_table || []).length,
        itemListElement: (page.comparison_table || []).map((row, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: row.feature,
          description: `Crown & Rose: ${row.crown_rose}. ${page.competitor_name}: ${row.competitor}.`,
        })),
      },

      // ── ItemList — Crown & Rose exclusive advantages ──────────────────────
      {
        '@type': 'ItemList',
        '@id':   `${url}#cr-advantages`,
        name:    `Crown & Rose advantages over ${page.competitor_name}`,
        description: `Capabilities available in Crown & Rose that ${page.competitor_name} does not offer.`,
        numberOfItems: (page.crown_rose_pros || []).length,
        itemListElement: (page.crown_rose_pros || []).map((pro, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: pro,
        })),
      },

      // ── FAQPage ───────────────────────────────────────────────────────────
      {
        '@type': 'FAQPage',
        '@id':   `${url}#faq`,
        name:    `${page.title} — FAQ`,
        url,
        mainEntity: (page.faq || []).map((f) => ({
          '@type': 'Question',
          name:    f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
    ],
  };
}

function ProConList({ items = [], positive }) {
  return (
    <ul className="space-y-1.5">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-[13px]">
          {positive
            ? <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            : <XCircle       className="h-4 w-4 text-red-400    flex-shrink-0 mt-0.5" />}
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function ComparisonPage() {
  const { slug } = useParams();
  const [page,     setPage]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    comparisonApi.getPage(slug)
      .then(setPage)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen gap-2 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin" /> Loading comparison…
    </div>
  );

  if (notFound || !page) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3">
      <h1 className="text-2xl font-bold">Comparison not found</h1>
      <p className="text-muted-foreground">This comparison page hasn't been generated yet.</p>
      <Link to="/" className="text-primary hover:underline">Back to Crown & Rose</Link>
    </div>
  );

  const pageUrl  = `${SITE_URL}/compare/${slug}`;
  const jsonLdGraph = buildGraph(page, slug);

  return (
    <>
      <Helmet>
        {/* ── Primary meta ──────────────────────────────────────────────── */}
        <title>{`${page.title || ''} | Crown & Rose`}</title>
        <meta name="description"  content={page.meta_description || ''} />
        <meta name="keywords"     content={page.meta_keywords    || ''} />
        <link rel="canonical"     href={pageUrl} />

        {/* ── Crawler directives ────────────────────────────────────────── */}
        {/* max-snippet:-1 lets Google display unlimited text snippets.      */}
        {/* max-image-preview:large enables rich image cards in SERPs.       */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />

        {/* ── Machine-readable alternate ────────────────────────────────── */}
        {/* Direct API scrapers and AI training crawlers can call this URL   */}
        {/* without executing JS to get all comparison data as clean JSON.   */}
        <link rel="alternate" type="application/json" href={`${API_ROOT}/compare/${slug}`} />

        {/* ── Open Graph (social + AI rich-card previews) ───────────────── */}
        <meta property="og:type"        content="article" />
        <meta property="og:title"       content={page.title || ''} />
        <meta property="og:description" content={page.meta_description || ''} />
        <meta property="og:url"         content={pageUrl} />
        <meta property="og:site_name"   content="Crown & Rose" />
        <meta property="og:image"       content="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/8d75a73125d9ed24cf3f04b732ce2568aaea3df859a8a9be1b31e43def8fb176.jpeg" />
        <meta property="og:image:width"  content="1264" />
        <meta property="og:image:height" content="848" />
        <meta property="og:image:alt"    content={`${page.title || 'Crown & Rose'} — Find your best writing tool`} />
        <meta property="og:locale"      content="en_US" />
        <meta property="article:author" content="Crown & Rose LLC" />

        {/* ── Twitter / X Card ──────────────────────────────────────────── */}
        <meta name="twitter:card"        content="summary_large_image" />
        <meta name="twitter:title"       content={page.title || ''} />
        <meta name="twitter:description" content={page.meta_description || ''} />
        <meta name="twitter:image"       content="https://static.prod-images.emergentagent.com/jobs/67d45115-9fac-4a11-82ed-b7a8bff08f0b/images/8d75a73125d9ed24cf3f04b732ce2568aaea3df859a8a9be1b31e43def8fb176.jpeg" />
        <meta name="twitter:image:alt"   content={`${page.title || 'Crown & Rose'} — Find your best writing tool`} />

        {/* ── Comprehensive JSON-LD @graph ──────────────────────────────── */}
        {/* Parsed by ALL crawlers before JS executes. Includes:             */}
        {/*   WebPage · Article · BreadcrumbList · SoftwareApplication ×2    */}
        {/*   ItemList (full comparison table) · ItemList (CR advantages)    */}
        {/*   FAQPage — for direct answer extraction by AI engines.          */}
        <script type="application/ld+json">
          {safeJsonLd(jsonLdGraph)}
        </script>
      </Helmet>

      {/* ── Public nav — crawlable site-wide navigation ──────────────── */}
      <PublicNavBar
        onSignIn={() => { window.location.href = '/login'; }}
        onSignUp={() => { window.location.href = '/'; }}
      />

      <div className="min-h-screen bg-background">
        {/* ── sr-only block — visible to screen readers, curl bots, ──── */}
        {/* simple scrapers that do not execute JavaScript.               */}
        {/* Contains the full comparison summary and feature table.       */}
        <section className="sr-only" aria-label="Comparison summary for screen readers and web crawlers">
          <h1>{page.title}</h1>
          <p>{page.summary_verdict}</p>

          <h2>Crown & Rose advantages over {page.competitor_name}</h2>
          <ul>
            {(page.crown_rose_pros || []).map((p, i) => <li key={i}>{p}</li>)}
          </ul>

          <h2>What writers look for in {page.competitor_name} — and how Crown &amp; Rose covers it</h2>
          <ul>
            {(page.cr_coverage || page.competitor_pros || []).map((p, i) => <li key={i}>{p}</li>)}
          </ul>

          <h2>Feature comparison: Crown &amp; Rose vs {page.competitor_name}</h2>
          <table>
            <caption>Crown &amp; Rose vs {page.competitor_name} — feature-by-feature comparison</caption>
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Crown &amp; Rose</th>
                <th scope="col">{page.competitor_name}</th>
              </tr>
            </thead>
            <tbody>
              {(page.comparison_table || []).map((row, i) => (
                <tr key={i}>
                  <th scope="row">{row.feature}</th>
                  <td>{row.crown_rose}</td>
                  <td>{row.competitor}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2>Frequently asked questions about Crown & Rose vs {page.competitor_name}</h2>
          {(page.faq || []).map((f, i) => (
            <div key={i}>
              <h3>{f.question}</h3>
              <p>{f.answer}</p>
            </div>
          ))}
        </section>

        {/* ── Visible breadcrumb nav ──────────────────────────────────── */}
        <nav aria-label="Breadcrumb" className="border-b border-border bg-card">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2 text-[12px] text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Crown &amp; Rose</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <Link to="/blog" className="hover:text-foreground transition-colors">Resources</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <span className="text-foreground" aria-current="page">{page.title}</span>
          </div>
        </nav>

        <main className="max-w-5xl mx-auto px-4 py-12 space-y-12">

          {/* ── Hero ─────────────────────────────────────────────────── */}
          <section className="space-y-4 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight leading-tight">
              {page.hero_headline}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{page.hero_subhead}</p>
            <div className="flex justify-center gap-3 pt-2">
              <Button asChild className="gap-1.5">
                <Link to="/"><Sparkles className="h-4 w-4" /> Try Crown &amp; Rose</Link>
              </Button>
            </div>
          </section>

          {/* ── Summary verdict ──────────────────────────────────────── */}
          <section aria-labelledby="verdict-heading" className="rounded-xl border border-border bg-muted/30 p-6">
            <h2 id="verdict-heading" className="text-base font-semibold mb-2">The short verdict</h2>
            <p className="text-[14px] leading-relaxed text-muted-foreground">{page.summary_verdict}</p>
          </section>

          {/* ── Feature-by-feature comparison table ──────────────────── */}
          {(page.comparison_table || []).length > 0 && (
            <section aria-labelledby="feature-table-heading">
              <h2 id="feature-table-heading" className="text-2xl font-bold mb-4">
                Feature-by-Feature Comparison
              </h2>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-[13px]" aria-label={`Crown & Rose vs ${page.competitor_name} feature comparison`}>
                  <caption className="sr-only">
                    Feature-by-feature comparison of Crown &amp; Rose versus {page.competitor_name}
                  </caption>
                  <thead>
                    <tr className="bg-muted/30 border-b border-border">
                      <th scope="col" className="text-left px-4 py-3 font-semibold text-[11.5px] uppercase tracking-wide text-muted-foreground">Feature</th>
                      <th scope="col" className="text-left px-4 py-3 font-semibold text-[11.5px] uppercase tracking-wide text-primary">Crown &amp; Rose</th>
                      <th scope="col" className="text-left px-4 py-3 font-semibold text-[11.5px] uppercase tracking-wide text-muted-foreground">{page.competitor_name}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.comparison_table.map((row, i) => (
                      <tr key={i} className={`border-t border-border ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                        <th scope="row" className="px-4 py-2.5 font-medium text-left">{row.feature}</th>
                        <td className="px-4 py-2.5 text-[12.5px]">{row.crown_rose}</td>
                        <td className="px-4 py-2.5 text-[12.5px] text-muted-foreground">{row.competitor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* ── Pros / Cons ───────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <section aria-labelledby="cr-pros-heading" className="rounded-xl border border-emerald-200 dark:border-emerald-800 p-5 space-y-3">
              <h2 id="cr-pros-heading" className="font-bold text-base flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" aria-hidden="true" /> Crown &amp; Rose advantages
              </h2>
              <ProConList items={page.crown_rose_pros} positive />
              {(page.crown_rose_cons || []).length > 0 && (
                <div className="pt-3 border-t border-border">
                  <p className="text-[11.5px] font-semibold text-muted-foreground mb-2">Limitations</p>
                  <ProConList items={page.crown_rose_cons} />
                </div>
              )}
            </section>
            <section aria-labelledby="comp-pros-heading" className="rounded-xl border border-border p-5 space-y-3">
              <h2 id="comp-pros-heading" className="font-bold text-base">{page.competitor_name} strengths</h2>
              <ProConList items={page.competitor_pros} positive />
              {(page.competitor_cons || []).length > 0 && (
                <div className="pt-3 border-t border-border">
                  <p className="text-[11.5px] font-semibold text-muted-foreground mb-2">Weaknesses</p>
                  <ProConList items={page.competitor_cons} />
                </div>
              )}
            </section>
          </div>

          {/* ── Body sections ────────────────────────────────────────── */}
          {(page.body_sections || []).map((section, i) => (
            <section key={i} className="prose prose-sm dark:prose-invert max-w-none space-y-2">
              <h2 className="text-xl font-bold">{section.heading?.replace(/^##\s*/, '')}</h2>
              <p className="text-[13.5px] text-muted-foreground leading-relaxed whitespace-pre-line">
                {section.body}
              </p>
            </section>
          ))}

          {/* ── Full multi-competitor matrix ──────────────────────────── */}
          <CompetitorMatrix />

          <div className="border-t border-border" role="separator" />

          {/* ── FAQ ──────────────────────────────────────────────────── */}
          {(page.faq || []).length > 0 && (
            <section aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
              <div className="space-y-4">
                {page.faq.map((item, i) => (
                  <div key={i} className="rounded-lg border border-border p-4">
                    <h3 className="font-semibold text-[14px] mb-1">{item.question}</h3>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── CTA ──────────────────────────────────────────────────── */}
          <section aria-labelledby="cta-heading" className="rounded-2xl border border-primary/20 bg-primary/5 p-8 text-center space-y-4">
            <h2 id="cta-heading" className="text-2xl font-bold">{page.cta_headline}</h2>
            <p className="text-[14px] text-muted-foreground max-w-xl mx-auto">{page.cta_body}</p>
            <Button asChild size="lg" className="gap-1.5">
              <Link to="/"><Sparkles className="h-4 w-4" /> Start writing with Crown &amp; Rose</Link>
            </Button>
          </section>

          {/* ── Other comparison pages ────────────────────────────────── */}
          <nav aria-label="Other comparisons" className="pt-4 border-t border-border">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              More comparisons
            </h2>
            <ul className="flex flex-wrap gap-2">
              {[
                { slug: 'crown-and-rose-vs-scrivener',     label: 'vs Scrivener'     },
                { slug: 'crown-and-rose-vs-atticus',       label: 'vs Atticus'       },
                { slug: 'crown-and-rose-vs-vellum',        label: 'vs Vellum'        },
                { slug: 'crown-and-rose-vs-prowritingaid', label: 'vs ProWritingAid' },
                { slug: 'crown-and-rose-vs-jasper',        label: 'vs Jasper'        },
                { slug: 'crown-and-rose-vs-novelcrafter',  label: 'vs Novelcrafter'  },
                { slug: 'crown-and-rose-vs-sudowrite',     label: 'vs Sudowrite'     },
                { slug: 'crown-and-rose-vs-novelai',       label: 'vs NovelAI'       },
                { slug: 'crown-and-rose-vs-grammarly',     label: 'vs Grammarly'     },
              ]
                .filter((c) => c.slug !== `crown-and-rose-vs-${slug?.replace('crown-and-rose-vs-', '')}`)
                .map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={`/compare/${c.slug}`}
                      className="rounded-full border border-border bg-background px-3.5 py-1 text-[12.5px] font-medium text-muted-foreground transition hover:border-primary hover:text-primary"
                    >
                      Crown &amp; Rose {c.label}
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>

        </main>
      </div>
    </>
  );
}
